import { Environment } from 'src/Environment';

import { request } from './request';
import { Steps, AcquiringChannelFlags } from '../context/salesforce-context';

export const CONSUMER_TYPE = {
  assistant: 'assistant',
  owner: 'owner or director',
};

export const BANK_ACCOUNT_HOLDER_TYPE = {
  PERSONAL: 'personal',
  BUSINESS: 'business',
};

export const BANK_ACCOUNT_BAV_STATUS = {
  VALID: 'Valid',
  INVALID: 'Invalid',
};

export const ID_TYPE = {
  SA_ID: 'id',
  PASSPORT: 'passport',
};

const authHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded',
  Origin: `${Environment.current?.uiHost}`,
  'Sec-Fetch-Site': 'cross-site',
  'Sec-Fetch-Mode': 'no-cors',
  'Sec-Fetch-Dest': 'empty',
  Referer: `${Environment.current?.uiHost}`,
};

const VODAPAY_KWIKA_NAME = Environment.current?.kwikaName;

const isResponseBodyContainsError = (response) => response && !response.ResponseData;
const isTokenResponseBodyContainsError = (response) =>
  response && (!response.access_token || !response.instance_url);

const getMerchantResponsePayload = (response) => {
  const merchant = JSON.parse(response?.result?.ResponseData);

  const deliveriesMap = merchant?.opportunity?.deliveries
    ? merchant.opportunity.deliveries.reduce((map, item) => {
        map[item.device_id] = item;
        return map;
      }, {})
    : {};

  const merchantResponse = {
    ...merchant,
    opportunity: {
      ...merchant?.opportunity,
      deliveries: deliveriesMap,
    },
  };

  return merchantResponse;
};

export const SalesforceService = (handleErrorResponse) => ({
  createAuthTokenAndInstanceUrl: async () => {
    const res = await request(
      'salesforce/token',
      {
        fetchOptions: {
          method: 'GET',
          headers: authHeaders,
        },
      },
      null,
      null,
      false,
    );

    if (res?.status === 200 && !isTokenResponseBodyContainsError(res?.result)) {
      return {
        access_token: res?.result?.access_token,
        instance_url: res?.result?.instance_url,
      };
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'Legacy: error response from vfs-token-service salesforce/token POST endpoint',
        false,
        false,
        res?.result,
      );
    }
  },
  createLead: async (
    instanceUrl,
    token,
    stepName,
    firstNames,
    mobileNumber,
    email,
    dealerId,
    agentId,
    salesOrg,
  ) => {
    const res = await request(
      'services/apexrest/OnboardingLead',
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify({
            step_name: stepName,
            lead: {
              first_names: firstNames,
              mobile_number: mobileNumber,
              email: email,
              footSoldier_ID: dealerId ? dealerId : '',
              agentId: agentId ? agentId : '',
              dealerId: salesOrg ? salesOrg : '',
            },
          }),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status === 200 && !isResponseBodyContainsError(res?.result)) {
      const response = JSON.parse(res.result.ResponseData);
      const leadIdValue = response?.lead?.id;
      return leadIdValue;
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/OnboardingLead POST endpoint (create lead)',
        false,
        false,
        res?.result,
      );
    }
  },
  updateLead: async (instanceUrl, token, stepName, leadId, lead) => {
    const res = await request(
      'services/apexrest/OnboardingLead',
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify({
            step_name: stepName,
            lead: {
              id: leadId,
              dealerId: lead.salesOrg,
              first_names: lead.firstNames,
              mobile_number: lead.mobileNumber,
              email: lead.email,
              type_of_business: lead.typeOfBusiness,
              monthly_card_turnover: lead.monthlyCardTurnover,
              solutionType: lead.solutionType,
              footSoldier_ID: lead.dealerId,
              agentId: lead.agentId,
              Source_System: lead.sourceSystem,
              device_type: lead.deviceType,
              device_quantity: lead.deviceQuantity,
              id_number: lead.idNumber,
              trading_as: lead.tradingAs,
              street: lead.street,
              suburb: lead.suburb,
              state: lead.province,
              region: lead.province,
              zip_code: lead.postalCode,
              dealer_id: lead.dealerIdVodacomBusiness,
              consumer_type: lead.consumerType,
            },
          }),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status === 200 && !isResponseBodyContainsError(res?.result)) {
      if (stepName === Steps.MONTHLY_CARD_TURNOVER) {
        return getMerchantResponsePayload(res);
      }
      return JSON.parse(res.result.ResponseData);
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/OnboardingLead POST endpoint (update lead)',
        false,
        false,
        res?.result,
      );
    }
  },
  updateMerchant: async (instanceUrl, token, merchant) => {
    const res = await request(
      `services/apexrest/Merchant`,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify(merchant),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status === 200 && !isResponseBodyContainsError(res?.result)) {
      return JSON.parse(res?.result?.ResponseData);
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/Merchant POST endpoint',
        false,
        false,
        res?.result,
      );
    }
  },
  getMerchant: async (instanceUrl, token, leadId) => {
    const res = await request(
      `services/apexrest/Merchant?leadId=${leadId}`,
      {
        fetchOptions: {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status === 200 && !isResponseBodyContainsError(res?.result)) {
      return JSON.parse(res?.result?.ResponseData);
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/Merchant GET endpoint',
        false,
        false,
        res?.result,
      );
    }
  },
  updateDocuments: async (instanceUrl, token, documents, accountId, contactId) => {
    const res = await request(
      `services/apexrest/OnboardingDocuments`,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify({
            ...documents,
            account_id: accountId,
            contact_id: contactId,
          }),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status !== 200 && handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/OnboardingDocuments POST endpoint',
        false,
        false,
        res?.result,
      );
    } else {
      return JSON.parse(res.result.ResponseData);
    }
  },

  deleteDocuments: async (instanceUrl, token, content_document_ids) => {
    const res = await request(
      `services/apexrest/OnboardingDocuments`,
      {
        fetchOptions: {
          method: 'DELETE',
          body: JSON.stringify({
            documents: content_document_ids.map((id) => {
              return { content_document_id: id };
            }),
          }),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status !== 200 && handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/OnboardingDocuments DELETE endpoint',
        false,
        false,
        res?.result,
      );
    }
  },

  getTerminals: async (instanceUrl, token) => {
    const res = await request(
      `services/apexrest/Terminals`,
      {
        fetchOptions: {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (res?.status === 200 && !isResponseBodyContainsError(res?.result)) {
      const terminals = JSON.parse(res?.result?.ResponseData)?.terminals || {};
      return terminals.reduce((map, item) => {
        if (!item.acquiring_channel) {
          map[item.id] =
            item.name.toUpperCase() === VODAPAY_KWIKA_NAME.toUpperCase()
              ? { ...item, acquiring_channel: AcquiringChannelFlags.buyInstoreFicaOnline }
              : { ...item, acquiring_channel: AcquiringChannelFlags.noAcquiringChannelSet };
          return map;
        } else {
          map[item.id] = item;
          return map;
        }
      }, {});
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/Terminals GET endpoint',
        false,
        false,
        res?.result,
      );
    }
  },
  requestOTP: async (instanceUrl, token, referenceNumber, emailAddress) => {
    const response = await request(
      `services/apexrest/OTP?refNumber=${referenceNumber}&emailAddress=${emailAddress}`,
      {
        fetchOptions: {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (response?.status === 200) {
      return response?.result;
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/OTP POST endpoint',
        false,
        false,
        response?.result,
      );
    }
  },
  submitOTP: async (instanceUrl, token, referenceNumber, emailAddress, otpCode) => {
    const response = await request(
      `services/apexrest/OTP?refNumber=${referenceNumber}&emailAddress=${emailAddress}&otpCode=${otpCode}`,
      {
        fetchOptions: {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        },
      },
      instanceUrl,
      null,
      false,
    );

    if (response?.status === 200) {
      return response?.result;
    } else if (handleErrorResponse) {
      handleErrorResponse(
        'error response from salesforce services/apexrest/OTP POST endpoint',
        false,
        false,
        response?.result,
      );
    }
  },
});
